/**
 * Loads `/sprite.svg` once per page lifetime and injects its <symbol> defs
 * into the document so that <use href="#id"> references resolve from the
 * same document with zero network traffic on subsequent renders.
 *
 * Without this, every <use href="/sprite.svg#id"> insertion (route change,
 * drawer open, stepper step, etc.) triggers a (re)validation HTTP request.
 *
 * Design notes:
 *   - Fire-and-forget: this MUST NOT block the React tree from rendering.
 *     Icons that mount before the sprite arrives will re-render via
 *     `subscribeSpriteReady` once injection completes.
 *   - Parsed via DOMParser (not innerHTML) to keep the injection path
 *     explicit and to avoid unintentionally executing inline event
 *     handlers if the sprite source ever moves to an untrusted origin.
 *   - The container uses `width:0; height:0; position:absolute` rather
 *     than `display:none`. Some WebKit/Edge versions stop resolving
 *     <use> references against subtrees with `display:none`. Do not
 *     "simplify" this without testing across browsers.
 */

export const SPRITE_PATH = '/sprite.svg';

const CONTAINER_ID = '__svg_sprite__';
const SPRITE_READY_EVENT = 'sprite:ready';

let loadPromise: Promise<boolean> | null = null;
let ready = false;

const isBrowser = (): boolean => typeof document !== 'undefined' && typeof window !== 'undefined';

const markReady = (): void => {
    if (ready) return;
    ready = true;
    document.dispatchEvent(new Event(SPRITE_READY_EVENT));
};

const injectSpriteMarkup = (svgText: string): void => {
    const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    const parserError = doc.querySelector('parsererror');
    if (parserError) {
        throw new Error('sprite.svg is not valid SVG');
    }
    const svg = doc.documentElement;
    if (!svg || svg.nodeName.toLowerCase() !== 'svg') {
        throw new Error('sprite.svg root is not an <svg> element');
    }

    const container = document.createElement('div');
    container.id = CONTAINER_ID;
    // See "Design notes" above re: avoiding display:none.
    container.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none;';
    container.appendChild(document.adoptNode(svg));
    document.body.appendChild(container);
};

/**
 * Idempotently fetches and injects the sprite. Resolves to `true` on
 * success, `false` on failure. Never rejects — callers do not need to
 * `.catch`. Safe to call multiple times; only one network request is made.
 */
export const loadSprite = (): Promise<boolean> => {
    if (!isBrowser() || typeof fetch !== 'function') {
        return Promise.resolve(false);
    }
    if (loadPromise) return loadPromise;

    // Already injected (e.g. HMR survivor). Fast-path.
    if (document.getElementById(CONTAINER_ID)) {
        markReady();
        loadPromise = Promise.resolve(true);
        return loadPromise;
    }

    loadPromise = (async () => {
        try {
            const res = await fetch(SPRITE_PATH, { credentials: 'omit' });
            if (!res.ok) {
                throw new Error(`HTTP ${res.status} ${res.statusText}`);
            }
            const text = await res.text();
            injectSpriteMarkup(text);
            markReady();
            return true;
        } catch (err) {
            // Allow a future retry by clearing the cached promise.
            loadPromise = null;
            // eslint-disable-next-line no-console
            console.error('[sprite-loader] failed to load sprite:', err);
            return false;
        }
    })();

    return loadPromise;
};

/** Whether the sprite has been injected into the document. */
export const isSpriteLoaded = (): boolean => ready;

/**
 * Subscribe to sprite-ready notifications. The listener fires exactly once
 * when the sprite becomes available. Returns an unsubscribe function. If
 * the sprite is already loaded, the listener is invoked asynchronously
 * via microtask.
 */
export const subscribeSpriteReady = (listener: () => void): (() => void) => {
    if (!isBrowser()) return () => undefined;
    if (ready) {
        queueMicrotask(listener);
        return () => undefined;
    }
    const handler = (): void => {
        document.removeEventListener(SPRITE_READY_EVENT, handler);
        listener();
    };
    document.addEventListener(SPRITE_READY_EVENT, handler);
    return () => document.removeEventListener(SPRITE_READY_EVENT, handler);
};

// Kick off the fetch as a side-effect of importing this module so callers
// only need `import './sprite-loader'`. Idempotent; safe under HMR.
if (isBrowser()) {
    void loadSprite();
}
